import { useEffect, useMemo } from 'react'
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAdminMappings, selectAdminMappings } from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'

function Field({ label, children, mono }) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled',
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontFamily: mono ? 'monospace' : 'inherit' }}>
        {children ?? '—'}
      </Typography>
    </Box>
  )
}

/**
 * Overview tab — business-facing summary of the selected KPI plus the actions
 * that operate on the whole record (edit metadata, soft delete). Data source
 * and SQL are intentionally not shown here; they live in the Formula tab.
 */
export default function Beacon4PerformanceNetworkOverviewAdminOverviewPanel({ config, onEdit, onClone, onDelete }) {
  const dispatch = useDispatch()
  const mappings = useSelector(selectAdminMappings)
  const isMain   = config.category === 'Main KPI'

  useEffect(() => {
    if (isMain) dispatch(fetchAdminMappings(config.id))
  }, [dispatch, config.id, isMain])

  const { subCount, failCount } = useMemo(() => {
    const active = mappings.filter(m => m.isActive)
    return {
      subCount:  active.filter(m => m.panel === 'Breakdown').length,
      failCount: active.filter(m => m.panel === 'Breakdown2').length,
    }
  }, [mappings])

  const slaText = (config.slaGreen != null || config.slaRed != null)
    ? `${config.slaGreen != null ? config.slaGreen.toFixed(2) : '—'} / ${config.slaRed != null ? config.slaRed.toFixed(2) : '—'}`
    : '—'

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Metadata</Typography>
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<EditIcon sx={{ fontSize: 16 }} />} size="small" variant="outlined"
            onClick={onEdit} sx={{ textTransform: 'none', mr: 1 }}>
            Edit metadata
          </Button>
          <Button startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} size="small" variant="outlined"
            onClick={onClone} sx={{ textTransform: 'none', mr: 1 }}>
            Clone
          </Button>
          <Button startIcon={<DeleteIcon sx={{ fontSize: 16 }} />} size="small" color="error"
            disabled={!config.isActive} onClick={onDelete} sx={{ textTransform: 'none' }}>
            Soft delete
          </Button>
        </Stack>

        <Stack direction="row" flexWrap="wrap" rowGap={2} columnGap={3}>
          <Field label="KPI key" mono>{config.kpiKey}</Field>
          <Field label="Name">{config.kpiName}</Field>
          <Field label="Category">{config.category}</Field>
          <Field label="Unit">{config.unit}</Field>
          <Field label="SLA green / red">{slaText}</Field>
          <Field label="Display order">{config.displayOrder}</Field>
          <Field label="Direction">{config.isRate ? 'Lower is better' : 'Higher is better'}</Field>
          <Field label="Status">
            {config.isActive
              ? <Chip label="Active" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
              : <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: 10 }} />}
          </Field>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Usage</Typography>
        {isMain ? (
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: 12 }}>
              Shown as a <strong>Main KPI</strong> on the Performance dashboard.
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Breakdown panels: <strong>{subCount}</strong> sub-KPIs · <strong>{failCount}</strong> failure counters.
              Configure these in the Breakdowns tab.
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            This is a <strong>{config.category}</strong> series. It appears inside the breakdown panels of the
            Main KPIs that reference it.
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          Source view: <span style={{ fontFamily: 'monospace' }}>{config.viewName || '—'}</span>
        </Typography>
      </Paper>
    </Box>
  )
}